import { map, switchMap, catchError, filter, takeUntil, startWith } from 'rxjs/operators'
import { defer, of, Observable, interval } from 'rxjs'
import { Epic, ofType } from 'redux-observable'
import { StateObservable } from 'redux-observable'
import { IStoreState } from '../../IStoreState'
import { requestDashboard, RequestDashboard, requestDashboardFailure, requestDashboardSuccess } from '../../actions/dashboard/RequestDashboard'
import DashboardActionTypes from '../../actions/dashboard/DashboardActionTypes'
import { ColumnRepository } from '../../../../repositories/column/ColumnRepository'
import { ColumnApiDataHandler } from '../../../../data-handlers/column/ColumnApiDataHandler'
import { requestAvailableDays, RequestAvailableDays, requestAvailableDaysFailure, requestAvailableDaysSuccess, RequestCreateLabels, requestCreateLabelsFailure, RequestCreateLabelsSuccess, requestCreateLabelsSuccess, RequestReprint, requestReprintFailure, requestReprintSuccess, RequestOrderGotIt, requestOrderGotItFailure, requestOrderGotItSuccess, RequestOrderGotItSuccess, RequestOrderStatusChange, requestOrderStatusChangeFailure, RequestOrderStatusChangeSuccess, requestOrderStatusChangeSuccess, RequestProductAccept, requestProductAcceptFailure, RequestProductAcceptSuccess, requestProductAcceptSuccess, RequestProductDecline, requestProductDeclineFailure, requestProductDeclineSuccess, RequestProductDeclineSuccess, RequestRemoveLabels, requestRemoveLabelsFailure, requestRemoveLabelsSuccess, RequestRemoveLabelsSuccess, requestVehicle, RequestVehicle, requestVehicleFailure, requestVehicleSuccess, setFailedOrderId, SetSelectedDate, RequestPrint, requestPrintSuccess, requestPrintFailure, RequestPrintSuccess, RequestSettings, requestSettingsSuccess, requestSettingsFailure, requestSettings, RequestSettingsSuccess, openConfirmDialog, ConfirmAcceptDeclinedProduct, requestProductAccept } from '../../actions/dashboard/DashboardAction'
import { AvailableDaysApiDataHandler } from '../../../../data-handlers/available-days/AvailableDaysApiDataHandler'
import { AvailableDaysRepository } from '../../../../repositories/available-days/AvailableDaysRepository'
import { OrderApiDataHandler } from '../../../../data-handlers/order/OrderApiDataHandler'
import { OrderRepository } from '../../../../repositories/order/OrderRepository'
import { LabelRepository } from '../../../../repositories/label/LabelRepository'
import { LabelApiDataHandler } from '../../../../data-handlers/label/LabelApiDataHandler'
import FailedOrderReason from '../../../../constants/FailedOrderReason'
import AuthActionTypes from '../../actions/auth/AuthActionTypes'
import { VehicleRepository } from '../../../../repositories/vehicle/VehicleRepository'
import { VehicleDataHandler } from '../../../../data-handlers/vehicle/VehicleApiDataHandler'
import { OrderStatus } from '../../../../entities/order/Order'
import { SettingsRepository } from '../../../../repositories/settings/SettingsRepository'
import { SettingsDataHandler } from '../../../../data-handlers/settings/SettingsApiDataHandler'
import { ColumnNumber } from '../../../../entities/column/Column'
import { isSameDay, startOfDay } from 'date-fns'

const columnRepository = new ColumnRepository(new ColumnApiDataHandler())
const orderRepository = new OrderRepository(new OrderApiDataHandler())
const availableDaysRepository = new AvailableDaysRepository(new AvailableDaysApiDataHandler())
const labelRepository = new LabelRepository(new LabelApiDataHandler())
const vehicleRepository = new VehicleRepository(new VehicleDataHandler())
const settingsRepository = new SettingsRepository(new SettingsDataHandler())

const vehiclesRequestInterval = 2 * 60 * 1000

const today = startOfDay(new Date())
const isTodaySelected = (state$: StateObservable<IStoreState>) => isSameDay(state$.value.Dashboard.selectedDate, today)

/**
 * Map set date to request dashboard
 */
export const mapSetDateToRequestDashboardEpic = (
  action$: Observable<
    SetSelectedDate
    | RequestProductAcceptSuccess
    | RequestProductDeclineSuccess
    | RequestPrintSuccess
    | RequestCreateLabelsSuccess
    | RequestRemoveLabelsSuccess
    | RequestOrderStatusChangeSuccess
    | RequestOrderGotItSuccess
  >,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
    ofType(
      DashboardActionTypes.SET_SELECTED_DATE,
      DashboardActionTypes.REQUEST_PRODUCT_ACCEPT_SUCCESS,
      DashboardActionTypes.REQUEST_PRODUCT_DECLINE_SUCCESS,
      DashboardActionTypes.REQUEST_PRINT_SUCCESS,
      DashboardActionTypes.REQUEST_CREATE_LABELS_SUCCESS,
      DashboardActionTypes.REQUEST_REMOVE_LABELS_SUCCESS,
      DashboardActionTypes.REQUEST_ORDER_STATUS_CHANGE_SUCCESS,
      DashboardActionTypes.REQUEST_ORDER_GOT_IT_SUCCESS
    ),
    switchMap((action) =>
      of(requestDashboard({
        date: state$.value.Dashboard.selectedDate,
        shouldResetOpenCycles: [DashboardActionTypes.SET_SELECTED_DATE].includes(action.type),
      }))
    )
  )

/**
 * Map set interval to request dashboard
 */
export const mapSetIntervalToRequestDashboardEpic = (
  action$: Observable<
    RequestSettingsSuccess
  >,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
    ofType(
      DashboardActionTypes.REQUEST_SETTINGS_SUCCESS
    ),
    switchMap((action) => interval(state$.value.Dashboard.refreshInterval).pipe(
      map(() => requestDashboard({
        date: state$.value.Dashboard.selectedDate,
        shouldResetOpenCycles: [DashboardActionTypes.SET_SELECTED_DATE].includes(action.type),
      })),
      takeUntil(action$.pipe(
        filter(() => !state$.value.Auth.isLogged)
      ))
    ))
  )

/**
 * Request columns for dashboard
 */
export const requestDashboardEpic = (
  action$: Observable<RequestDashboard>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_DASHBOARD),
    switchMap((action) =>
      defer(
        () => columnRepository.getByDate(action.payload.date)
      ).pipe(
        map((columns) =>
          requestDashboardSuccess({ columns, shouldResetOpenCycles: action.payload.shouldResetOpenCycles })
        ),
        catchError((error) => of(
          requestDashboardFailure({
            date: action.payload.date,
            error,
          })
        ))
      )
    )
  )

/**
 * Map actions to requestVehicleEpic
 */
 export const mapSetIntervalToRequestVehicleEpic: Epic = (
  action$,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
    ofType(
      'APP_INIT',
      AuthActionTypes.REQUEST_LOGIN_SUCCESS
    ),
    filter(() => state$.value.Auth.isLogged),
    switchMap(() =>
      interval(vehiclesRequestInterval).pipe(
        filter(() => isTodaySelected(state$)),
        map(() => requestVehicle()),
        takeUntil(action$.pipe(
          filter(() => !state$.value.Auth.isLogged)
        ))
      )
    )
  )

export const mapToRequestVehicleEpic: Epic = (
    action$: Observable<SetSelectedDate>,
    state$: StateObservable<IStoreState>
  ) =>
    action$.pipe(
      ofType(DashboardActionTypes.SET_SELECTED_DATE),
      filter(() => isTodaySelected(state$)),
      switchMap(() => of(requestVehicle()))
    )

/**
 * Request vehicle to pickup
 */
export const requestVehicleEpic = (
  action$: Observable<RequestVehicle>,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_VEHICLE),
    filter(() => isTodaySelected(state$)),
    switchMap(() =>
      defer(
        () => vehicleRepository.getVehicles()
      ).pipe(
        takeUntil(action$.pipe(
          filter(() => !isTodaySelected(state$))
        )),
        map((vehicles) => requestVehicleSuccess({ vehicles })),
        catchError((error) => of(
          requestVehicleFailure({
            error,
          })
        ))
      )
    )
  )

/**
 * Map actions to requestAvailableDaysEpic
 */
export const mapToRequestAvailableDaysEpic: Epic = (
  action$,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
    ofType(
      DashboardActionTypes.REQUEST_DASHBOARD_SUCCESS
    ),
    filter(() => state$.value.Auth.isLogged),
    switchMap(() =>
      of(requestAvailableDays())
    )
  )

/**
 * Request days with available orders for dashboard
 */
export const requestAvailableDaysEpic = (
  action$: Observable<RequestAvailableDays>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_AVAILABLE_DAYS),
    switchMap(() =>
      defer(
        () => availableDaysRepository.get()
      ).pipe(
        map((availableDays) =>
          requestAvailableDaysSuccess({ availableDays })
        ),
        catchError((error) => of(
          requestAvailableDaysFailure({
            error,
          })
        ))
      )
    )
  )


/**
 * Confirm accept declined product
 */
export const confirmAcceptDeclinedProductEpic = (
  action$: Observable<ConfirmAcceptDeclinedProduct>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.CONFIRM_ACCEPT_DECLINED_PRODUCT),
    switchMap((action) => defer(
      () => orderRepository.changeStatus(action.payload.product.order.orderId, OrderStatus.ACCEPTED)
    ).pipe(
      map(() =>
        requestProductAccept({
          product: action.payload.product,
          forceAccept: true,
        })
      ),
      catchError((error) => of(
        requestOrderStatusChangeFailure({
          orderId: action.payload.product.order.orderId,
          error,
        })
      ))
    ))
  )

/**
 * Request accept a product
 */
export const requestProductAcceptEpic = (
  action$: Observable<RequestProductAccept>,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_PRODUCT_ACCEPT),
    switchMap((action) => {
      const isOrderReady = state$.value.Dashboard.columns[ColumnNumber.READY].getAllOrders()
        .some(order => order.orderId === action.payload.product.order.orderId)

      const isProductDeclined = action.payload.product.isDeclined

      if (isOrderReady && isProductDeclined && !action.payload.forceAccept) {
        return of(openConfirmDialog(action.payload.product))
      }

      return defer(() => orderRepository.acceptProduct(action.payload.product)).pipe(
        map(() =>
          requestProductAcceptSuccess({
            product: action.payload.product,
          })
        ),
        catchError((error) => of(
          requestProductAcceptFailure({
            product: action.payload.product,
            error,
          })
        ))
      )
    }
    )
  )

/**
 * Request decline a product
 */
export const requestProductDeclineEpic = (
  action$: Observable<RequestProductDecline>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_PRODUCT_DECLINE),
    switchMap((action) =>
      defer(
        () => orderRepository.declineProduct(
          action.payload.product, action.payload.declineData
        )
      ).pipe(
        map(() =>
          requestProductDeclineSuccess({
            product: action.payload.product,
          })
        ),
        catchError((error) => of(
          requestProductDeclineFailure({
            product: action.payload.product,
            error,
          })
        ))
      )
    )
  )


/**
* Request print
*/
export const requestPrintEpic = (
  action$: Observable<RequestPrint>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_PRINT),
    switchMap((action) =>
      defer(
        () => labelRepository.print(action.payload.product)
      ).pipe(
        map(() =>
          requestPrintSuccess({
            product: action.payload.product,
          })
        ),
        catchError((error) => of(
          requestPrintFailure({
            product: action.payload.product,
            error,
          })
        ))
      )
    )
  )


/**
* Request reprint
*/
export const requestReprintEpic = (
  action$: Observable<RequestReprint>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_REPRINT),
    switchMap((action) =>
      defer(
        () => labelRepository.rePrint(action.payload.product)
      ).pipe(
        map(() =>
          requestReprintSuccess({
            product: action.payload.product,
          })
        ),
        catchError((error) => of(
          requestReprintFailure({
            product: action.payload.product,
            error,
          })
        ))
      )
    )
  )


/**
 * Request create labels
 */
export const requestCreateLabelsEpic = (
  action$: Observable<RequestCreateLabels>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_CREATE_LABELS),
    switchMap((action) =>
      defer(
        () => labelRepository.createBatch(action.payload.products)
      ).pipe(
        map(() =>
          requestCreateLabelsSuccess({
            products: action.payload.products,
          })
        ),
        catchError((error) => of(
          requestCreateLabelsFailure({
            products: action.payload.products,
            error,
          })
        ))
      )
    )
  )

/**
 * Request remove labels
 */
export const requestRemoveLabelsEpic = (
  action$: Observable<RequestRemoveLabels>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_REMOVE_LABELS),
    switchMap((action) =>
      defer(
        () => labelRepository.removeBatch(action.payload.products)
      ).pipe(
        map(() =>
          requestRemoveLabelsSuccess({
            products: action.payload.products,
          })
        ),
        catchError((error) => of(
          requestRemoveLabelsFailure({
            products: action.payload.products,
            error,
          })
        ))
      )
    )
  )

/**
 * Request order status change
 */
export const requestOrderStatusChangeEpic = (
  action$: Observable<RequestOrderStatusChange>,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_ORDER_STATUS_CHANGE),
    switchMap((action) => {

      if (action.payload.nextStatus !== OrderStatus.CANCELED) {
        const isNotAllProductsProcessed = state$.value.Dashboard.columns[ColumnNumber.INCOMING].getAllOrders()
          .find(order => order.orderId === action.payload.orderId)
          ?.products.some(product => !product.isCanceled && !product.isDeclined)

        if (isNotAllProductsProcessed) {
          return of(setFailedOrderId(action.payload.orderId, FailedOrderReason.NOT_ALL_PRODUCTS_PROCESSED))
        }

        const isNotAllProductsPrepared = state$.value.Dashboard.columns[ColumnNumber.IN_PROGRESS].getAllOrders()
          .find(order => order.orderId === action.payload.orderId)
          ?.products
          .some(product => !product.isLabeled && !product.isCanceled && !product.isDeclined)

        if (isNotAllProductsPrepared) {
          return of(setFailedOrderId(action.payload.orderId, FailedOrderReason.NOT_ALL_PRODUCTS_PREPARED))
        }
      }

      return defer(
        () => orderRepository.changeStatus(action.payload.orderId, action.payload.nextStatus)
      ).pipe(
        map(() =>
          requestOrderStatusChangeSuccess({
            orderId: action.payload.orderId,
          })
        ),
        catchError((error) => of(
          requestOrderStatusChangeFailure({
            orderId: action.payload.orderId,
            error,
          })
        ))
      )
    })
  )

/**
 * Request order status change
 */
export const requestOrderGotItEpic = (
  action$: Observable<RequestOrderGotIt>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_ORDER_GOT_IT),
    switchMap((action) => {
      return defer(
        () => orderRepository.gotIt(action.payload.orderId, action.payload.itemId)
      ).pipe(
        map(() =>
          requestOrderGotItSuccess({
            orderId: action.payload.orderId,
          })
        ),
        catchError((error) => of(
          requestOrderGotItFailure({
            orderId: action.payload.orderId,
            error,
          })
        ))
      )
    })
  )


/**
 * Map actions to requestDashboardSettingsEpic
 */
export const mapToRequestDashboardSettingsEpic: Epic = (
  action$,
  state$: StateObservable<IStoreState>
) =>
  action$.pipe(
    ofType(
      AuthActionTypes.REQUEST_LOGIN_SUCCESS,
      AuthActionTypes.SET_IS_LOGGED
    ),
    filter(() => state$.value.Auth.isLogged),
    switchMap(() =>
      of(requestSettings())
    )
  )


/**
 * Request dashboard settings
 */
export const requestDashboardSettingsEpic = (
  action$: Observable<RequestSettings>
) =>
  action$.pipe(
    ofType(DashboardActionTypes.REQUEST_SETTINGS),
    switchMap(() =>
      defer(
        () => settingsRepository.requestSettings()
      ).pipe(
        map(({ storeName, refreshInterval }) =>
          requestSettingsSuccess({ storeName, refreshInterval })
        ),
        catchError((error) => of(
          requestSettingsFailure({
            error,
          })
        ))
      )
    )
  )


export const dashboardEpics = [
  mapToRequestAvailableDaysEpic,
  requestAvailableDaysEpic,
  mapSetDateToRequestDashboardEpic,
  mapSetIntervalToRequestDashboardEpic,
  requestDashboardEpic,

  mapSetIntervalToRequestVehicleEpic,
  mapToRequestVehicleEpic,
  requestVehicleEpic,

  confirmAcceptDeclinedProductEpic,
  requestProductAcceptEpic,
  requestProductDeclineEpic,
  requestPrintEpic,
  requestReprintEpic,
  requestCreateLabelsEpic,
  requestRemoveLabelsEpic,

  requestOrderStatusChangeEpic,
  requestOrderGotItEpic,

  mapToRequestDashboardSettingsEpic,
  requestDashboardSettingsEpic,
]
